import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/SEO';

/**
 * Shared chrome for the Privacy / Terms / Cookies pages.
 *
 * Keeps them visually consistent with the marketing landing page
 * (Figtree headings, teal accents, soft warm background) while
 * staying lightweight — no shadcn dependencies, no heavy state.
 *
 * Each child page passes its own <SEO /> props plus body content.
 */

export function LegalLayout({
  title,
  description,
  canonical,
  lastUpdated,
  children,
}) {
  return (
    <>
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        image="https://kidswiseapp.com/og-image.png"
      />
      <div
        data-testid="legal-page-root"
        style={{
          minHeight: '100vh',
          background: '#FFFBEA',
          color: '#1f2933',
          fontFamily: 'Figtree, system-ui, sans-serif',
        }}
      >
        <header
          style={{
            padding: '1.1rem 1.5rem',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            background: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(8px)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: 980,
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Link
              to="/"
              data-testid="legal-home-link"
              style={{
                fontWeight: 800,
                color: '#0e7490',
                textDecoration: 'none',
                fontSize: '1.05rem',
                letterSpacing: '-0.01em',
              }}
            >
              ← Kids WiseApp
            </Link>
            <nav
              style={{
                display: 'flex',
                gap: '1.1rem',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              <Link to="/privacy" style={legalNavLink} data-testid="legal-nav-privacy">Privacy</Link>
              <Link to="/terms" style={legalNavLink} data-testid="legal-nav-terms">Terms</Link>
              <Link to="/cookies" style={legalNavLink} data-testid="legal-nav-cookies">Cookies</Link>
            </nav>
          </div>
        </header>

        <main
          style={{
            maxWidth: 760,
            margin: '0 auto',
            padding: '3rem 1.5rem 4rem',
          }}
        >
          <h1
            data-testid="legal-page-title"
            style={{
              fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              marginBottom: '0.5rem',
              color: '#062e3d',
            }}
          >
            {title}
          </h1>
          {lastUpdated && (
            <p
              data-testid="legal-last-updated"
              style={{
                color: '#586474',
                fontSize: '0.9rem',
                marginBottom: '2rem',
              }}
            >
              Last updated: {lastUpdated}
            </p>
          )}

          {/*
            These legal documents are DRAFT scaffolds. They are NOT
            legal advice. The platform owner is responsible for having
            them reviewed by a UK-childcare-aware solicitor before going
            into commercial use. The disclaimer below stays visible to
            visitors so nobody mistakes the scaffold for a finalised
            policy.
          */}
          <div
            data-testid="legal-draft-banner"
            style={{
              background: 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.38)',
              padding: '0.9rem 1rem',
              borderRadius: 10,
              fontSize: '0.86rem',
              lineHeight: 1.45,
              marginBottom: '2rem',
              color: '#7a4a08',
            }}
          >
            <strong>Draft scaffold —</strong> this document is a working
            draft pending solicitor review. The final, legally binding
            version will be published before public launch. If you need
            specific clarifications in the meantime, contact{' '}
            <a
              href="mailto:support@kidswiseapp.com"
              style={{ color: '#7a4a08', fontWeight: 700 }}
            >
              support@kidswiseapp.com
            </a>
            .
          </div>

          <div
            data-testid="legal-content"
            style={{
              fontSize: '1rem',
              lineHeight: 1.7,
              color: '#1f2933',
            }}
          >
            {children}
          </div>
        </main>

        <footer
          style={{
            padding: '1.6rem 1.5rem',
            borderTop: '1px solid rgba(0,0,0,0.06)',
            color: '#586474',
            fontSize: '0.82rem',
          }}
        >
          <div
            style={{
              maxWidth: 980,
              margin: '0 auto',
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>© {new Date().getFullYear()} Kids WiseApp Ltd.</span>
            <span style={{ display: 'flex', gap: '1rem' }}>
              <Link to="/privacy" style={legalNavLink}>Privacy</Link>
              <Link to="/terms" style={legalNavLink}>Terms</Link>
              <Link to="/cookies" style={legalNavLink}>Cookies</Link>
              <a href="mailto:support@kidswiseapp.com" style={legalNavLink}>Support</a>
            </span>
          </div>
        </footer>
      </div>
    </>
  );
}

export function H2({ children, id }) {
  return (
    <h2
      id={id}
      style={{
        fontSize: '1.35rem',
        fontWeight: 700,
        marginTop: '2.2rem',
        marginBottom: '0.6rem',
        color: '#062e3d',
      }}
    >
      {children}
    </h2>
  );
}

export function P({ children }) {
  return (
    <p style={{ margin: '0 0 0.9rem 0' }}>{children}</p>
  );
}

export function UL({ children }) {
  return (
    <ul style={{ margin: '0 0 1rem 1.2rem', padding: 0 }}>{children}</ul>
  );
}

const legalNavLink = {
  color: '#0e7490',
  textDecoration: 'none',
  fontWeight: 600,
};

export default LegalLayout;
